import { centsToDollars, formatDollars } from '@fruitful/fruitils';
import { ApexDerivedSubState, type ImAccount } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import {
    ArrowRightIcon,
    BodySmallRegular,
    ChartPieIcon,
    ClipboardCheckIcon,
    CogIcon,
    DownloadIcon,
    FileTextIcon,
    FolderOpenIcon,
    FootnoteRegular,
    FullScreenSpinner,
    H1Emphasized,
    Icon,
    IconButton,
    isNonMobileWeb,
    LastUpdated,
    NavigationRow,
    Notice,
    RowList,
    SCREEN_CONTAINER_HORIZONTAL_PADDING,
    ScreenContainer,
    Spinner,
    // TextRow,
    TileButton,
    UploadIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useShowGuideMessaging } from '../../../guideMessaging/hooks/useShowGuideMessaging';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';
import { useGetApexActivities } from '../../hooks/useGetApexActivities';
import { useGetApexAccountPerformance } from '../../hooks/useGetApexPerformance';
import { selectedApexTransferAccount } from '../../state/investmentAtoms';
import type {
    ApexActivity,
    PendingApexAccountTransaction,
} from '../../types/activities';
import {
    // type ApexAccountAllTimePerformance,
    type ApexPerformanceDatapoint,
    ApexPerformanceHorizon,
} from '../../types/performance';
import {
    ApexAccountHorizonSelectionRow,
    ApexAccountPerformanceGraph,
    ApexPerformanceBadges,
    ApexSnapshotRow,
} from '../performance/ApexPerformance';
import { Transactions } from '../transactions/ApexAccountTransactions';

const RecentTransactions = ({
    transactions,
    pendingTransactions,
    apexAccountDetails,
}: {
    transactions: ApexActivity[];
    pendingTransactions: PendingApexAccountTransaction[];
    apexAccountDetails: ImAccount;
}) => {
    const sortedTransactions = [...transactions].sort((a, b) => {
        const { day: aDay, month: aMonth, year: aYear } = a.activityDate;
        const { day: bDay, month: bMonth, year: bYear } = b.activityDate;
        const aDate = new Date(aYear, aMonth - 1, aDay);
        const bDate = new Date(bYear, bMonth - 1, bDay);
        return bDate.getTime() - aDate.getTime();
    });

    const pendingShow = pendingTransactions.slice(0, 3);
    const postedShow = sortedTransactions.slice(
        0,
        Math.max(0, 3 - pendingShow.length),
    );
    const hasAny = pendingShow.length > 0 || postedShow.length > 0;

    return (
        <RowList
            width="100%"
            rowHeaderProps={{
                title: 'Recent transactions',
                rightContent: hasAny ? (
                    <XStack
                        alignItems="center"
                        gap={4}
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/account-transactions',
                                params: {
                                    apexAccountId:
                                        apexAccountDetails.apexAccountId,
                                },
                            });
                        }}
                        cursor="pointer"
                    >
                        <BodySmallRegular maxWidth={200} textAlign="right">
                            View all
                        </BodySmallRegular>
                        <Icon icon={ArrowRightIcon} size={16} />
                    </XStack>
                ) : null,
            }}
        >
            {!hasAny ? (
                <View
                    padding={24}
                    backgroundColor="$gray100"
                    borderBottomLeftRadius={16}
                    borderBottomRightRadius={16}
                >
                    <BodySmallRegular>No transactions found</BodySmallRegular>
                </View>
            ) : (
                <Transactions
                    transactions={postedShow}
                    pendingTransactions={pendingShow}
                    apexAccountDetails={apexAccountDetails}
                />
            )}
        </RowList>
    );
};

// const AllTimePerformance = ({
//     allTimePerformance,
// }: {
//     allTimePerformance?: ApexAccountAllTimePerformance;
// }) => {
//     if (allTimePerformance == null) return null;

//     return (
//         <RowList
//             width="100%"
//             rowHeaderProps={{
//                 title: 'All time performance',
//             }}
//         >
//             <TextRow
//                 key="deposits"
//                 title="Deposits"
//                 secondaryText={formatDollars(allTimePerformance?.deposits)}
//             />
//             <TextRow
//                 key="withdrawals"
//                 title="Withdrawals"
//                 secondaryText={formatDollars(allTimePerformance?.withdrawals)}
//             />
//             <TextRow
//                 key="dividends"
//                 title="Dividends and interest"
//                 secondaryText={formatDollars(allTimePerformance?.dividends)}
//             />
//         </RowList>
//     );
// };

export const ApexAccountOverview = () => {
    useTrackViewOnFocus('Investment/Overview');

    const showGuideMessaging = useShowGuideMessaging();

    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const [performanceSnapshot, setPerformanceSnapshot] =
        useState<ApexPerformanceDatapoint | null>(null);

    const [selectedPerformanceHorizon, setSelectedPerformanceHorizon] =
        useState<ApexPerformanceHorizon>(ApexPerformanceHorizon.ALL);

    const [isChartScrollLocked, setIsChartScrollLocked] = useState(false);

    const onPerformanceActiveDatapointChange = useCallback(
        (datapoint: ApexPerformanceDatapoint | null) => {
            setPerformanceSnapshot(datapoint);
        },
        [],
    );

    // dataUpdatedAt is a React Query timestamp (ms) set only on successful fetches.
    // It does not change on failed requests or when cached data is reused without a network call.
    const {
        isLoading: isLoadingDetails,
        data: apexAccountDetails,
        dataUpdatedAt,
    } = useGetApexAccountDetails(apexAccountId);

    const { isLoading: isLoadingTransactions, data: transactionsResponse } =
        useGetApexActivities(apexAccountId);

    const { data: performanceData, isLoading: isPerformanceLoading } =
        useGetApexAccountPerformance(apexAccountId);

    const selectedPerformanceSeries = useMemo(() => {
        return performanceData?.[selectedPerformanceHorizon] ?? null;
    }, [performanceData, selectedPerformanceHorizon]);

    const hasPerformanceGraphData =
        (selectedPerformanceSeries?.datapoints.length ?? 0) > 0;

    const setSelectedApexTransferAccount = useSetAtom(
        selectedApexTransferAccount,
    );

    const { width: windowWidth } = useWindowDimensions();

    const isFundableAccount = apexAccountDetails?.canDepositFunds ?? false;
    const canWithdraw = apexAccountDetails?.canWithdrawFunds ?? false;

    const restrictionAlert = useMemo(() => {
        if (
            apexAccountDetails?.derivedSubState ===
                ApexDerivedSubState.OPEN_IN_CLOSING ||
            apexAccountDetails?.derivedSubState === ApexDerivedSubState.CLOSED
        ) {
            return (
                <View width="100%">
                    <Notice
                        variant="warning"
                        title="Closed account"
                        description="Your account has been closed. Please reach out to our Hospitality team for more info at support@fruitful.com."
                    />
                </View>
            );
        }

        if (
            apexAccountDetails?.derivedSubState ===
            ApexDerivedSubState.OPEN_RESTRICTED
        ) {
            return (
                <View width="100%">
                    <Notice
                        variant="danger"
                        title="Restricted account"
                        description="Your account has been restricted. Please reach out to our Hospitality team for more info at support@fruitful.com."
                    />
                </View>
            );
        }
        return null;
    }, [apexAccountDetails]);

    if (isLoadingDetails || isLoadingTransactions) {
        return <FullScreenSpinner />;
    }

    if (apexAccountDetails == null) {
        router.navigate('/home/investment');
        return;
    }

    return (
        <ScreenContainer
            hasBackButton={true}
            onBack={() => {
                router.navigate({
                    pathname: '/home/investment',
                });
            }}
            title={apexAccountDetails.nickname}
            onFallbackRoute="/home/investment"
            scrollEnabled={!isChartScrollLocked}
            rightAction={
                IS_PRODUCTION ? null : (
                    <IconButton
                        variant="tertiary"
                        icon={CogIcon}
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/account-dev',
                                params: {
                                    apexAccountId,
                                },
                            });
                        }}
                    />
                )
            }
        >
            <YStack gap={16}>
                {/* Only specify height if we are rendering the graph and the snapshot overlays. */}
                <YStack
                    height={hasPerformanceGraphData ? 115 : undefined}
                    padding={16}
                    gap={12}
                >
                    {performanceSnapshot == null ? (
                        <>
                            <H1Emphasized>
                                {formatDollars(
                                    centsToDollars(
                                        apexAccountDetails.realtimeBalanceData
                                            .accountEquityCents,
                                    ),
                                )}
                            </H1Emphasized>
                            <ApexPerformanceBadges
                                performanceSeries={selectedPerformanceSeries}
                            />
                        </>
                    ) : (
                        <ApexSnapshotRow
                            performanceSnapshot={performanceSnapshot}
                        />
                    )}
                </YStack>

                {isPerformanceLoading && (
                    <YStack paddingVertical={48}>
                        <Spinner />
                    </YStack>
                )}

                {hasPerformanceGraphData && (
                    <YStack
                        backgroundColor={isNonMobileWeb ? '$gray100' : '$white'}
                        borderRadius={24}
                        overflow="visible"
                        paddingVertical={16}
                    >
                        {isNonMobileWeb ? (
                            <ApexAccountPerformanceGraph
                                backgroundColor="$gray100"
                                datapoints={
                                    selectedPerformanceSeries?.datapoints ?? []
                                }
                                onActiveDatapointChange={
                                    onPerformanceActiveDatapointChange
                                }
                                onScrollLock={setIsChartScrollLocked}
                            />
                        ) : (
                            <YStack
                                alignSelf="center"
                                marginHorizontal={
                                    -SCREEN_CONTAINER_HORIZONTAL_PADDING
                                }
                                width={windowWidth}
                            >
                                <ApexAccountPerformanceGraph
                                    backgroundColor="$white"
                                    datapoints={
                                        selectedPerformanceSeries?.datapoints ??
                                        []
                                    }
                                    onActiveDatapointChange={
                                        onPerformanceActiveDatapointChange
                                    }
                                    onScrollLock={setIsChartScrollLocked}
                                />
                            </YStack>
                        )}

                        <ApexAccountHorizonSelectionRow
                            selectedPerformanceHorizon={
                                selectedPerformanceHorizon
                            }
                            onSelect={setSelectedPerformanceHorizon}
                        />
                    </YStack>
                )}

                <YStack gap={12}>
                    <XStack gap={12}>
                        <TileButton
                            tileProps={{
                                paddingVertical: 16,
                                disabled: !isFundableAccount,
                            }}
                            text="Fund account"
                            icon={DownloadIcon}
                            onPress={() => {
                                setSelectedApexTransferAccount(
                                    apexAccountDetails,
                                );
                                router.navigate(
                                    '/investments/transfer/deposit',
                                );
                            }}
                        />
                        <TileButton
                            tileProps={{
                                paddingVertical: 16,
                                disabled: !canWithdraw,
                            }}
                            text="Withdraw"
                            icon={UploadIcon}
                            onPress={() => {
                                setSelectedApexTransferAccount(
                                    apexAccountDetails,
                                );
                                router.navigate(
                                    '/investments/transfer/withdrawal',
                                );
                            }}
                        />
                    </XStack>
                    <XStack gap={12}>
                        <TileButton
                            tileProps={{
                                paddingVertical: 16,
                            }}
                            text="Composition"
                            icon={ChartPieIcon}
                            onPress={() => {
                                router.navigate({
                                    pathname: '/investments/account-portfolio',
                                    params: {
                                        apexAccountId,
                                    },
                                });
                            }}
                        />
                        <TileButton
                            tileProps={{
                                paddingVertical: 16,
                            }}
                            text="Account details"
                            icon={FileTextIcon}
                            onPress={() => {
                                router.navigate({
                                    pathname: '/investments/account-details',
                                    params: {
                                        apexAccountId,
                                    },
                                });
                            }}
                        />
                    </XStack>
                </YStack>

                {restrictionAlert}

                {/* <AllTimePerformance
                    allTimePerformance={allTimePerformance}
                /> */}

                <RecentTransactions
                    transactions={transactionsResponse?.transactions ?? []}
                    pendingTransactions={
                        transactionsResponse?.pendingTransactions ?? []
                    }
                    apexAccountDetails={apexAccountDetails}
                />

                <RowList>
                    <NavigationRow
                        leftIcon={FolderOpenIcon}
                        title="Documents"
                        subtitle="Statements • Trades"
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/investment-statements',
                                params: { apexAccountId },
                            });
                        }}
                    />
                    <NavigationRow
                        leftIcon={ClipboardCheckIcon}
                        title="Tax forms"
                        subtitle="1099s • Tax reports"
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/tax-forms',
                                params: { apexAccountId },
                            });
                        }}
                    />
                </RowList>

                <View>
                    <Notice
                        variant="neutral"
                        title="How do I close an account?"
                        description="Message your Guide to close an investment account."
                        action={{
                            text: 'Message my Guide',
                            onPress: () => {
                                showGuideMessaging();
                            },
                        }}
                    />
                </View>

                <FootnoteRegular color="$gray900">
                    Investments are held at and cleared by APEX Clearing
                    Corporation, member FINRA/SIPC.
                </FootnoteRegular>
                <LastUpdated dataUpdatedAt={dataUpdatedAt} />
            </YStack>
        </ScreenContainer>
    );
};
